import { Inject, Injectable, NgZone } from '@angular/core';
import S3 from 'aws-sdk/clients/s3';
import { NzUploadFile } from 'ng-zorro-antd/upload';
import { environment } from 'src/environments/environment';
import { APIService } from './api.service';
import { ManagedUpload } from 'aws-sdk/lib/s3/managed_upload';
import { DOCUMENT } from '@angular/common';
import { NzSafeAny } from 'ng-zorro-antd/core/types';

import SendData = ManagedUpload.SendData;
import { LoggingService } from './logging.service';

@Injectable({
  providedIn: 'root',
})
export class MediaService {
  constructor(
    private readonly apiService: APIService,
    private readonly ngZone: NgZone,
    private readonly logger: LoggingService,
    @Inject(DOCUMENT) private readonly doc: NzSafeAny,
  ) {}

  public async uploadNzFile(
    uid: string,
    fileToUpload: NzUploadFile,
    data: string,
    s3Path: string,
    objContextPath: string,
    fileList: NzUploadFile[],
  ): Promise<void> {
    const BASE64_MARKER = ';base64,';
    const base64Index = data.indexOf(BASE64_MARKER) + BASE64_MARKER.length;
    const buffer = this.base64ToArrayBuffer(data.substring(base64Index));
    const upload = new S3.ManagedUpload({
      params: {
        Bucket: environment.RESTRICTED_BUCKET,
        Key: `form-upload/${s3Path}/${
          this.apiService.userIdentityId
        }/${objContextPath}/${uid}/${fileToUpload.name.trim()}`,
        Body: buffer,
      },
    });
    return upload
      .promise()
      .then(async (sendData: SendData) => {
        if (!sendData) {
          throw new Error('Invalid upload response');
        }
        const fileFromList = fileList.find((file) => file.uid === uid);

        if (fileFromList) {
          fileFromList.originalFileName = fileFromList.originFileObj?.name;

          fileFromList.url = await new S3({ signatureVersion: 'v4' }).getSignedUrlPromise('getObject', {
            Bucket: sendData.Bucket,
            Key: sendData.Key,
            Expires: 1200,
            ResponseContentDisposition: `attachment; filename=${encodeURIComponent(fileToUpload.name)}`,
          });
          fileFromList.response = sendData;
          /*if (this.isImageFileType(fileToUpload.type as string)) {
            const thumb = await this.generateImageThumbnail(uid, fileToUpload, s3Path, objContextPath, fileList, data);
            fileFromList.thumbUrl = thumb.Location;
          }*/
        } else {
          this.logger.error(new Error('File not found in provided list ' + uid), false);
        }
      })
      .catch((error) => {
        this.logger.error(error);
        fileList = fileList.filter((file) => file.uid !== uid);
      });
  }

  public async generateImageThumbnail(
    uid: string,
    fileToUpload: NzUploadFile,
    s3Path: string,
    formDefinitionId: string,
    fileList: NzUploadFile[],
    data: string | null,
  ): Promise<SendData> {
    return new Promise((resolve, reject) => {
      this.ngZone.runOutsideAngular(() => {
        const canvas = this.doc.createElement('canvas');
        canvas.width = this.thumbnailSize;
        canvas.height = this.thumbnailSize;
        canvas.style.cssText = `position: fixed; left: 0; top: 0; width: ${this.thumbnailSize}px; height: ${this.thumbnailSize}px; z-index: 9999; display: none;`;
        this.doc.body.appendChild(canvas);
        const ctx = canvas.getContext('2d');
        const img = new Image();
        img.onload = (): void => {
          const { width, height } = img;

          let drawWidth = this.thumbnailSize;
          let drawHeight = this.thumbnailSize;
          let offsetX = 0;
          let offsetY = 0;

          if (width < height) {
            drawHeight = height * (this.thumbnailSize / width);
            offsetY = -(drawHeight - drawWidth) / 2;
          } else {
            drawWidth = width * (this.thumbnailSize / height);
            offsetX = -(drawWidth - drawHeight) / 2;
          }

          ctx.drawImage(img, offsetX, offsetY, drawWidth, drawHeight);
          canvas.toBlob((blob: any) => {
            this.doc.body.removeChild(canvas);

            new S3.ManagedUpload({
              params: {
                Bucket: environment.BUCKET_DYNAMICFORM_THUMBNAILS,
                Key: `dynamic-form-thumbnails/${
                  this.apiService.userIdentityId
                }/${s3Path}/${formDefinitionId}/${uid}/${fileToUpload.name.trim()}.png`,
                Body: blob,
                ACL: 'public-read',
              },
            })
              .promise()
              .then((value) => {
                resolve(value);
              })
              .catch((err) => {
                this.logger.error(err);
                fileList = fileList.filter((fileFromList) => fileFromList.uid !== uid);
                reject(err);
              });
          });
        };
        img.src = data ? data : window.URL.createObjectURL(fileToUpload.originFileObj as Blob);
      });
    });
  }

  private base64ToArrayBuffer(base64: string): ArrayBufferLike {
    const binary_string = window.atob(base64);
    const len = binary_string.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = binary_string.charCodeAt(i);
    }
    return bytes.buffer;
  }

  private readonly thumbnailSize = 200;
  private readonly isImageFileType = (type: string): boolean => !!type && type.indexOf('image/') === 0;
}
